const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const mergeRecords = (
  original: Record<string, unknown>,
  form: Record<string, unknown>,
): Record<string, unknown> => {
  const result: Record<string, unknown> = { ...original };

  for (const [key, value] of Object.entries(form)) {
    // senhub reads config keys case-insensitively, so a stored "SenPIC" is the
    // form's "Senpic": replace it rather than keep both.
    const storedKey = Object.keys(original).find(
      (k) => k.toLowerCase() === key.toLowerCase(),
    );
    if (storedKey !== undefined) {
      delete result[storedKey];
    }

    if (value === undefined) {
      continue;
    }

    const current = storedKey === undefined ? undefined : original[storedKey];
    if (!isRecord(value)) {
      result[key] = value;
      continue;
    }

    const merged = mergeRecords(isRecord(current) ? current : {}, value);
    // A form section with nothing set (e.g. empty per-product prompts) does
    // not add an empty object the stored config never had.
    if (current === undefined && Object.keys(merged).length === 0) {
      continue;
    }
    result[key] = merged;
  }

  return result;
};

/**
 * Lays the form-mode config over the customer's stored config, so keys the form
 * does not know (e.g. AlertReviewer.ReviewValueJsonEnabled, ContextData) survive
 * a Form save. Hasura `_set` replaces the whole column, so without this every
 * such key is deleted.
 *
 * Form values win: an `undefined` form value removes the key (a cleared field),
 * and arrays are replaced, not merged. The legacy root `Senbos` / `StagingSenbos`
 * are dropped because the form has already moved them under `Senpic`.
 */
export const mergeFormConfigIntoOriginal = (
  original: unknown,
  formConfig: Record<string, unknown>,
): Record<string, unknown> => {
  const merged = mergeRecords(isRecord(original) ? original : {}, formConfig);
  delete merged.Senbos;
  delete merged.StagingSenbos;
  return merged;
};
